import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, RefreshCw, Copy, Palette, Wrench } from "lucide-react";

/**
 * PhotoHealthCard
 *
 * Reads the cached `/admin/photos/corrupted` endpoint and shows admin how many
 * members have missing/undersized photo files. Provides a rescan button
 * (blocking ~1 minute) and a modal that lists everyone with contact info so
 * the admin can chase them for a re-upload.
 */
export default function PhotoHealthCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [colorFixing, setColorFixing] = useState(false);
    const [repairing, setRepairing] = useState(false);
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/photos/corrupted");
            setData(data);
        } catch (e) {
            // Silent by design — the dashboard shouldn't crash if the endpoint
            // fails — but log so intermittent failures surface in the console.
            console.warn("PhotoHealthCard: load failed", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const rescan = async () => {
        setScanning(true);
        try {
            await api.post("/admin/photos/scan");
            toast.success("Photo scan complete");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Scan failed");
        } finally {
            setScanning(false);
        }
    };

    const fixColorMode = async () => {
        if (!window.confirm("Convert CMYK member photos to sRGB so browsers can render them? This runs in the background — you can leave the page.")) return;
        setColorFixing(true);
        try {
            await api.post("/admin/photos/fix-color-mode");
            toast.info("Color-mode fix started — this can take a couple of minutes.");
            const started = Date.now();
            while (Date.now() - started < 6 * 60 * 1000) {
                await new Promise(r => setTimeout(r, 5000));
                const { data: st } = await api.get("/admin/photos/fix-color-mode/status");
                if (st?.state === "done") {
                    toast.success("Color-mode fix complete", {
                        description: `${st.converted} converted · ${st.skipped} already OK · ${st.errors} errors`,
                    });
                    break;
                }
                if (st?.state === "error") {
                    toast.error(`Color-mode fix failed: ${st.error || "unknown"}`);
                    break;
                }
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Color-mode fix failed");
        } finally {
            setColorFixing(false);
        }
    };

    const repairMissing = async () => {
        try {
            // 1) Preview
            setRepairing(true);
            const { data: preview } = await api.post("/admin/photos/repair", null, { params: { dry_run: true } });
            const total = preview?.total_pointers_cleared || 0;
            if (total === 0) {
                toast.success("Nothing to repair — every DB photo pointer resolves.");
                await load();
                return;
            }
            const ok = window.confirm(
                `Repair scan finished:\n` +
                `• Missing files in storage: ${preview.missing_files_in_storage}\n` +
                `• Person main photos to clear: ${preview.persons_cleared_main}\n` +
                `• Person spouse photos to clear: ${preview.persons_cleared_spouse}\n` +
                `• Member main photos to clear: ${preview.members_cleared_main}\n` +
                `• Member spouse photos to clear: ${preview.members_cleared_spouse}\n\n` +
                `Clear ${total} DB pointer${total === 1 ? "" : "s"} whose files no longer exist?\n` +
                `(Snapshotted to data_audit for rollback.)`
            );
            if (!ok) {
                toast.message("Repair cancelled.");
                return;
            }
            const { data: applied } = await api.post("/admin/photos/repair", null, { params: { dry_run: false } });
            toast.success(
                `Repair applied — ${applied.total_pointers_cleared} DB pointer${applied.total_pointers_cleared === 1 ? "" : "s"} cleared.`,
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Photo repair failed");
        } finally {
            setRepairing(false);
        }
    };

    const copyList = async () => {
        const lines = data.members.map((m) =>
            `${m.name}\t${m.phone || ""}\t${m.kind}\t${m.status}\t${m.filename}`
        );
        const text = "Name\tPhone\tKind\tStatus\tFilename\n" + lines.join("\n");
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Copy failed");
        }
    };

    const count = data?.count ?? 0;
    const lastScan = data?.last_scan_at ? new Date(data.last_scan_at).toLocaleString() : "never";

    return (
        <>
            <div
                className={`rounded-2xl border p-5 flex items-center gap-4 ${
                    count > 0 ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-200"
                }`}
                data-testid="photo-health-card"
            >
                <div className={`h-11 w-11 rounded-lg grid place-items-center ${
                    count > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                }`}>
                    <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="label-eyebrow">Photo health</p>
                    <p className="rotary-heading text-lg leading-tight text-rotary-ink">
                        {loading ? "Checking…" : count > 0
                            ? `${count} member${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} a fresh photo`
                            : "All member photos look good"}
                    </p>
                    <p className="text-[11px] text-muted-foreground pt-0.5">
                        Last scan: {lastScan}
                        {data?.threshold_bytes ? ` · Minimum size: ${Math.round(data.threshold_bytes / 1024)} KB` : ""}
                    </p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                        size="sm"
                        variant={count > 0 ? "default" : "outline"}
                        onClick={() => setOpen(true)}
                        disabled={loading || count === 0}
                        data-testid="photo-health-view-btn"
                    >
                        View list
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={rescan}
                        disabled={scanning}
                        data-testid="photo-health-rescan-btn"
                    >
                        <RefreshCw className={`mr-1 h-3.5 w-3.5 ${scanning ? "animate-spin" : ""}`} />
                        {scanning ? "Scanning…" : "Rescan"}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={fixColorMode}
                        disabled={colorFixing}
                        data-testid="photo-health-fix-color-btn"
                        title="Convert CMYK photos to sRGB so browsers can render them"
                    >
                        <Palette className={`mr-1 h-3.5 w-3.5 ${colorFixing ? "animate-pulse" : ""}`} />
                        {colorFixing ? "Fixing…" : "Fix colors"}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={repairMissing}
                        disabled={repairing}
                        data-testid="photo-health-repair-btn"
                        title="Detect DB photo pointers whose files no longer exist in Object Storage, and clear them (with rollback snapshot)"
                    >
                        <Wrench className={`mr-1 h-3.5 w-3.5 ${repairing ? "animate-pulse" : ""}`} />
                        {repairing ? "Repairing…" : "Repair missing"}
                    </Button>
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col" data-testid="photo-health-dialog">
                    <DialogHeader>
                        <DialogTitle className="rotary-heading text-2xl flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                            Members needing a fresh photo
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto">
                        {count === 0 ? (
                            <p className="text-sm text-muted-foreground py-8 text-center">
                                No corrupted photos. 🎉
                            </p>
                        ) : (
                            <div className="rounded-lg border overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted text-muted-foreground text-xs uppercase tracking-wide">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium">Member</th>
                                            <th className="text-left px-3 py-2 font-medium">Contact</th>
                                            <th className="text-left px-3 py-2 font-medium">Photo</th>
                                            <th className="text-left px-3 py-2 font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.members.map((m, i) => (
                                            <tr
                                                key={`${m.filename}-${i}`}
                                                className="border-t"
                                                data-testid={`photo-health-row-${i}`}
                                            >
                                                <td className="px-3 py-2">
                                                    <div className="font-medium text-rotary-ink">{m.name || "—"}</div>
                                                    <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{m.filename}</div>
                                                </td>
                                                <td className="px-3 py-2 tabular-nums">{m.contact_channel || m.phone || "—"}</td>
                                                <td className="px-3 py-2">
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${
                                                        m.kind === "spouse"
                                                            ? "bg-pink-100 text-pink-700"
                                                            : "bg-blue-100 text-blue-700"
                                                    }`}>{m.kind}</span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${
                                                        m.status === "missing"
                                                            ? "bg-red-100 text-red-700"
                                                            : "bg-amber-100 text-amber-800"
                                                    }`}>
                                                        {m.status === "missing" ? "File missing" : `Only ${m.size_bytes} B`}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2 sm:justify-between">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={copyList}
                            disabled={count === 0}
                            data-testid="photo-health-copy-btn"
                        >
                            <Copy className="mr-1.5 h-4 w-4" />
                            Copy list
                        </Button>
                        <Button type="button" onClick={() => setOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
